import { Box, Button, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';

import colors from 'common/constants/colors';
import { countLabel } from '../utils/ndcProductLink';
import LinkFactChip, { LINK_FACT } from './LinkFactChip';

const describeDefaultNdc = ({ ndc, isReplaced, newDefaultNdc }) =>
  isReplaced
    ? `${ndc} stops being the default NDC when you confirm. ${newDefaultNdc} becomes the default.`
    : `${ndc} is the NDC dispensed by default for this product.`;

// POM-6600 — the NDCs already linked to a product, one row each with its manufacturer. The default
// NDC carries a chip in the row's corner, struck through when confirming will replace it. Rows arrive
// from core in NDC order. With `visibleRowLimit`, longer lists show that many rows and "Show all".
const LinkedNdcRows = ({ ndcs, replacedDefaultNdc, newDefaultNdc, visibleRowLimit }) => {
  const [isShowingAll, setIsShowingAll] = useState(false);
  const isTrimmed = Boolean(visibleRowLimit) && ndcs.length > visibleRowLimit;
  const visibleNdcs = isTrimmed && !isShowingAll ? ndcs.slice(0, visibleRowLimit) : ndcs;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {visibleNdcs.map((linkedNdc) => {
        const isReplaced = linkedNdc.isDefault && linkedNdc.ndc === replacedDefaultNdc;
        return (
          <Box
            key={linkedNdc.ndc}
            data-testid='linked-ndc-row'
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              bgcolor: colors.dialogSurfaceGrey,
            }}
          >
            <Typography variant='body2' sx={{ fontWeight: 700 }}>
              {linkedNdc.ndc}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {linkedNdc.manufacturer || 'No manufacturer'}
            </Typography>
            {linkedNdc.isDefault && (
              <Tooltip
                arrow
                title={describeDefaultNdc({ ndc: linkedNdc.ndc, isReplaced, newDefaultNdc })}
              >
                <Box
                  component='span'
                  data-testid='default-ndc-chip'
                  data-replaced={isReplaced ? 'true' : 'false'}
                  sx={{ ml: 'auto', display: 'inline-flex' }}
                >
                  <LinkFactChip fact={LINK_FACT.DEFAULT} isStruckThrough={isReplaced} />
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      })}
      {isTrimmed && (
        <Button
          size='small'
          onClick={() => setIsShowingAll((wasShowingAll) => !wasShowingAll)}
          sx={{ alignSelf: 'flex-start', px: 0, minWidth: 0, textTransform: 'none' }}
        >
          {isShowingAll ? 'Show fewer' : `Show all ${countLabel(ndcs.length, 'NDC', 'NDCs')}`}
        </Button>
      )}
    </Box>
  );
};

LinkedNdcRows.propTypes = {
  ndcs: PropTypes.arrayOf(
    PropTypes.shape({
      ndc: PropTypes.string.isRequired,
      manufacturer: PropTypes.string,
      isDefault: PropTypes.bool,
    })
  ).isRequired,
  replacedDefaultNdc: PropTypes.string,
  newDefaultNdc: PropTypes.string,
  visibleRowLimit: PropTypes.number,
};
LinkedNdcRows.defaultProps = {
  replacedDefaultNdc: undefined,
  newDefaultNdc: undefined,
  visibleRowLimit: undefined,
};

export default LinkedNdcRows;
